/**
 * Tunnel d'onboarding pour les nouveaux comptes (pseudo ou OAuth).
 * Affiché par App.tsx tant que `session.isNewAccount === true`.
 *
 * Deux étapes (approche douce "freemium") :
 *   1. Avatar — choix d'un avatar (random emoji par défaut, personnalisable
 *      via AvatarEditor, photo / initiales / carte).
 *   2. Espaces de jeu — l'utilisateur indique ce qui l'intéresse (Détente
 *      pré-cochée car gratuit ; Compétition optionnelle).
 *
 * NOTE : il n'y a PAS d'étape "abonnement Stripe" en sortie d'onboarding.
 * Les utilisateurs intéressés par la compétition entrent dans l'espace en
 * mode "visiteur" (accès partiel, sans abonnement) et seront sollicités
 * contextuellement pour s'abonner via `<CompetitionUpsellBanner />` et le
 * gating des actions payantes — cf. docs/onboarding/PARTIAL_ACCESS.md
 *
 * Les choix sont sauvegardés via /api/onboarding/choices pour permettre à
 * l'équipe d'analyser le funnel et de relancer les "wants_competition" qui
 * n'ont pas encore souscrit.
 */

import { useCallback, useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import WTMonogram from '../components/WTMonogram';
import { PlayerAvatar, invalidateAvatarCache } from '../components/PlayerAvatar';
import AvatarEditor from '../components/AvatarEditor';
import { pickRandomDefaultAvatar, type DefaultAvatarChoice } from '../lib/avatarDefaults';
import { saveOnboardingChoices } from '../lib/onboardingApi';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import '../styles/login.css';
import '../styles/account-onboarding.css';

const AVATAR_OUTPUT_SIZE = 256;

type StepId = 'avatar' | 'spaces';

interface AccountOnboardingProps {
  session: Session;
  /** Appelé quand l'onboarding est terminé (skip ou validation). */
  onCompleted: () => void;
}

export default function AccountOnboarding({ session, onCompleted }: AccountOnboardingProps) {
  const [step, setStep] = useState<StepId>('avatar');
  const [wantsDetente, setWantsDetente] = useState(true);
  const [wantsCompetition, setWantsCompetition] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const visibleSteps: StepId[] = ['avatar', 'spaces'];
  const stepIndex = visibleSteps.indexOf(step);
  const totalSteps = visibleSteps.length;

  const goBack = () => {
    setGlobalError(null);
    if (step === 'spaces') setStep('avatar');
  };

  const finishOnboarding = useCallback(() => {
    setGlobalError(null);
    onCompleted();
  }, [onCompleted]);

  const handleAvatarDone = () => {
    setStep('spaces');
  };
  const handleAvatarSkip = () => {
    // Skip total de l'onboarding : on n'enregistre pas de préférences,
    // l'utilisateur entre direct dans l'app.
    finishOnboarding();
  };

  const handleSpacesContinue = async () => {
    setGlobalError(null);
    if (!session.token) {
      setGlobalError('Session expirée, veuillez vous reconnecter.');
      return;
    }
    try {
      await saveOnboardingChoices(session.token, {
        wants_detente: wantsDetente,
        wants_competition: wantsCompetition,
        subscribe_intent: 'none',
      });
      finishOnboarding();
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement.');
    }
  };

  return (
    <div className="login-page account-onboarding-page">
      <div className="login-bg" />

      <div className="login-card login-card-wide account-onboarding-card">
        <div className="login-header">
          <WTMonogram />
          <h1 className="login-title">Bienvenue {session.pseudoDisplay} !</h1>
          <p className="login-tagline">
            {step === 'avatar' && 'Choisissez votre avatar pour commencer'}
            {step === 'spaces' && 'Vos centres d\'intérêt'}
          </p>
          <div className="account-onboarding-progress" aria-label={`Étape ${stepIndex + 1} sur ${totalSteps}`}>
            {visibleSteps.map((s, i) => (
              <span
                key={s}
                className={`account-onboarding-progress-dot${i <= stepIndex ? ' done' : ''}${i === stepIndex ? ' current' : ''}`}
              />
            ))}
            <span className="account-onboarding-progress-text">
              Étape {stepIndex + 1} / {totalSteps}
            </span>
          </div>
        </div>

        <div className="login-body account-onboarding-body">
          {globalError && <div className="login-error">{globalError}</div>}

          {step === 'avatar' && (
            <AvatarStep
              session={session}
              onContinue={handleAvatarDone}
              onSkip={handleAvatarSkip}
            />
          )}

          {step === 'spaces' && (
            <SpacesStep
              wantsDetente={wantsDetente}
              wantsCompetition={wantsCompetition}
              setWantsDetente={setWantsDetente}
              setWantsCompetition={setWantsCompetition}
              onBack={goBack}
              onContinue={handleSpacesContinue}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Étape 1 — Choix de l'avatar
// ════════════════════════════════════════════════════════════════════

function AvatarStep({
  session,
  onContinue,
  onSkip,
}: {
  session: Session;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const [current, setCurrent] = useState<DefaultAvatarChoice>(() => pickRandomDefaultAvatar());
  const [showEditor, setShowEditor] = useState(false);
  const [busy, setBusy] = useState<'upload' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedViaEditor, setSavedViaEditor] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const renderPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const size = AVATAR_OUTPUT_SIZE;
    canvas.width = size;
    canvas.height = size;
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = current.bgColor;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'white';
    ctx.font = `${Math.floor(size * 0.55)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(current.emoji, size / 2, size / 2 + size * 0.03);
  }, [current]);

  useEffect(() => {
    renderPreview();
  }, [renderPreview]);

  const regenerate = () => {
    setError(null);
    setSavedViaEditor(false);
    setCurrent((prev) => pickRandomDefaultAvatar(prev));
  };

  const uploadCurrentCanvas = async (): Promise<void> => {
    const canvas = canvasRef.current;
    if (!canvas) throw new Error('Aperçu indisponible');

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas to blob failed'))),
        'image/png',
        0.95,
      );
    });

    const arrayBuffer = await blob.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        '',
      ),
    );

    const res = await fetch(`${BACKEND_URL}/api/avatars/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pseudo: session.pseudo,
        image: base64,
        format: 'png',
        token: session.token || '',
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || data.message || `Erreur ${res.status}`);
    }
  };

  const handleContinue = async () => {
    setError(null);
    setBusy('upload');
    try {
      if (!savedViaEditor) {
        await uploadCurrentCanvas();
      }
      invalidateAvatarCache(session.pseudo);
      onContinue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement');
    } finally {
      setBusy(null);
    }
  };

  const handleEditorSaved = () => {
    setSavedViaEditor(true);
    invalidateAvatarCache(session.pseudo);
    setShowEditor(false);
  };

  const initialState = useMemo(
    () => ({
      mode: 'emoji' as const,
      emojis: [current.emoji],
      emojiBgColor: current.bgColor,
      emojiBgGradient: null,
    }),
    [current],
  );

  return (
    <>
      <div className="account-onboarding-preview">
        {savedViaEditor ? (
          <PlayerAvatar
            pseudo={session.pseudo}
            size={180}
            cacheBust={Date.now()}
            photoUrl={`${BACKEND_URL}/api/avatars/${encodeURIComponent(session.pseudo)}?size=360&t=${Date.now()}`}
          />
        ) : (
          <canvas
            ref={canvasRef}
            className="account-onboarding-canvas"
            width={AVATAR_OUTPUT_SIZE}
            height={AVATAR_OUTPUT_SIZE}
            aria-label="Aperçu de l'avatar"
          />
        )}
      </div>

      <p className="account-onboarding-hint">
        Un emoji aléatoire vous est proposé. Vous pouvez le régénérer,
        le personnaliser, ou continuer pour le conserver.
      </p>

      {error && <div className="login-error">{error}</div>}

      <div className="account-onboarding-actions">
        <button
          type="button"
          className="account-onboarding-btn account-onboarding-btn-secondary"
          onClick={regenerate}
          disabled={busy !== null || savedViaEditor}
          title="Tirer un nouvel emoji au hasard"
        >
          <span className="account-onboarding-btn-icon" aria-hidden="true">🎲</span>
          <span>Régénérer</span>
        </button>

        <button
          type="button"
          className="account-onboarding-btn account-onboarding-btn-secondary"
          onClick={() => setShowEditor(true)}
          disabled={busy !== null}
          title="Choisir manuellement (emoji, initiales, carte, photo)"
        >
          <span className="account-onboarding-btn-icon" aria-hidden="true">🎨</span>
          <span>Personnaliser</span>
        </button>

        <button
          type="button"
          className="account-onboarding-btn account-onboarding-btn-primary"
          onClick={handleContinue}
          disabled={busy !== null}
        >
          {busy === 'upload' ? 'Enregistrement…' : 'Continuer →'}
        </button>
      </div>

      <button
        type="button"
        className="account-onboarding-skip"
        onClick={onSkip}
        disabled={busy !== null}
      >
        Passer cette étape
      </button>

      <p className="account-onboarding-note">
        Vous pourrez modifier votre avatar à tout moment depuis « Paramètres ».
      </p>

      {showEditor && (
        <AvatarEditor
          pseudo={session.pseudo}
          token={session.token || ''}
          onClose={() => setShowEditor(false)}
          onSaved={handleEditorSaved}
          initialState={initialState}
          skipExistingAvatarLoad
        />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Étape 2 — Espaces de jeu
// ════════════════════════════════════════════════════════════════════

function SpacesStep({
  wantsDetente,
  wantsCompetition,
  setWantsDetente,
  setWantsCompetition,
  onBack,
  onContinue,
}: {
  wantsDetente: boolean;
  wantsCompetition: boolean;
  setWantsDetente: (v: boolean) => void;
  setWantsCompetition: (v: boolean) => void;
  onBack: () => void;
  onContinue: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canContinue = wantsDetente || wantsCompetition;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canContinue) {
      setError('Veuillez sélectionner au moins un espace.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onContinue();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="account-onboarding-form">
      <p className="account-onboarding-hint">
        Quels espaces de jeu vous intéressent&nbsp;? Vous pourrez explorer
        chaque espace dès maintenant — les fonctions avancées de l'espace
        compétition (inscription aux tournois, coach IA…) nécessitent un
        abonnement que vous pourrez prendre quand vous le souhaiterez.
      </p>

      <label
        className={`onboarding-space-card${wantsDetente ? ' selected' : ''}`}
        htmlFor="space-detente"
      >
        <input
          id="space-detente"
          type="checkbox"
          checked={wantsDetente}
          onChange={(e) => setWantsDetente(e.target.checked)}
        />
        <div className="onboarding-space-card-body">
          <div className="onboarding-space-card-title">
            <span className="onboarding-space-icon" aria-hidden="true">🎴</span>
            Détente <span className="onboarding-space-badge free">gratuit</span>
          </div>
          <p className="onboarding-space-desc">
            Tarot à 3, 4 ou 5 joueurs · parties contre l'IA · chat et forums ·
            classements détente · bibliothèque de parties.
          </p>
        </div>
      </label>

      <label
        className={`onboarding-space-card${wantsCompetition ? ' selected' : ''}`}
        htmlFor="space-competition"
      >
        <input
          id="space-competition"
          type="checkbox"
          checked={wantsCompetition}
          onChange={(e) => setWantsCompetition(e.target.checked)}
        />
        <div className="onboarding-space-card-body">
          <div className="onboarding-space-card-title">
            <span className="onboarding-space-icon" aria-hidden="true">🏆</span>
            Compétition fédérale <span className="onboarding-space-badge paid">aperçu gratuit</span>
          </div>
          <p className="onboarding-space-desc">
            Tournois FFT à 4 joueurs · championnat individuel CIA · championnat
            par équipes · coach IA d'analyse · bibliothèque illimitée.
            <br />
            <em>Visite libre&nbsp;; un abonnement débloquera la participation aux tournois.</em>
          </p>
        </div>
      </label>

      {error && <div className="login-error">{error}</div>}

      <div className="account-onboarding-actions">
        <button
          type="button"
          className="account-onboarding-btn account-onboarding-btn-secondary"
          onClick={onBack}
          disabled={busy}
        >
          ← Précédent
        </button>
        <button
          type="submit"
          className="account-onboarding-btn account-onboarding-btn-primary"
          disabled={busy || !canContinue}
        >
          {busy ? 'Enregistrement…' : 'Continuer →'}
        </button>
      </div>
    </form>
  );
}

